/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  LightningIcon,
  GearIcon,
  SparkleIcon,
  ChartLineUpIcon,
  PlayIcon,
  StopIcon,
  CaretLeftIcon,
  PlugIcon,
  ChatCircleTextIcon,
  BroadcastIcon,
  SpeakerHighIcon,
  SpeakerSlashIcon,
  SkipForwardIcon,
  TimerIcon,
  CopyIcon,
  DownloadSimpleIcon,
  RecordIcon,
  ChartBarIcon,
} from "@phosphor-icons/react";
import { api, getOrCreateSession, wsChatUrl, BACKEND_URL } from "@/lib/api";
import SettingsPanel from "@/components/SettingsPanel";
import ConnectPanel from "@/components/ConnectPanel";
import StatsPanel from "@/components/StatsPanel";
import AgentVisualizer from "@/components/AgentVisualizer";
import ChatQueue from "@/components/ChatQueue";
import RelayShare from "@/components/RelayShare";
import TrialBadge from "@/components/TrialBadge";
import VipStatusCard from "@/components/VipStatusCard";
import ProTrialHook from "@/components/ProTrialHook";
import HighlightsFeed from "@/components/HighlightsFeed";
import RecapShareModal from "@/components/RecapShareModal";
import FirstRunWelcome from "@/components/FirstRunWelcome";
import Logo from "@/components/Logo";

const Dashboard = () => {
  const sessionId = useMemo(() => getOrCreateSession(), []);
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const [channel, setChannel] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [stats, setStats] = useState({ total_messages: 0, messages_read: 0, top_chatters: [] });
  const [agentEnabled, setAgentEnabled] = useState(false);
  const [muted, setMuted] = useState(false);
  const [mobileTab, setMobileTab] = useState("agent");
  const [connectedSince, setConnectedSince] = useState(null);
  const [uptime, setUptime] = useState("");
  const [recapOpen, setRecapOpen] = useState(false);

  const wsRef = useRef(null);
  const audioRef = useRef(null);
  const queueRef = useRef([]);
  const processingRef = useRef(false);
  const settingsRef = useRef(null);
  const agentEnabledRef = useRef(false);

  useEffect(() => {
    (async () => {
      const r = await api.get(`/settings/${sessionId}`);
      setSettings(r.data);
      settingsRef.current = r.data;
    })();
    openWS();
    refreshStats();
    const t = setInterval(refreshStats, 10000);
    return () => clearInterval(t);
  }, [sessionId]); // eslint-disable-line

  useEffect(() => { settingsRef.current = settings; }, [settings]);
  useEffect(() => { agentEnabledRef.current = agentEnabled; }, [agentEnabled]);

  // Session uptime ticker
  useEffect(() => {
    if (!connectedSince) { setUptime(""); return; }
    const tick = () => {
      const ms = Date.now() - connectedSince;
      const s = Math.floor(ms / 1000);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      setUptime(h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}` : `${m}:${String(sec).padStart(2, "0")}`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [connectedSince]);

  const refreshStats = async () => {
    try {
      const r = await api.get(`/stats/${sessionId}`);
      setStats(r.data);
    } catch (e) { /* noop */ }
  };

  const openWS = () => {
    if (wsRef.current) {
      try { wsRef.current.close(); } catch (e) { /* noop */ }
    }
    const ws = new WebSocket(wsChatUrl(sessionId));
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      try {
        const payload = JSON.parse(ev.data);
        if (payload.type === "status") {
          if (payload.status === "connected") {
            setConnected(true);
            setChannel(payload.channel);
            setConnectedSince(Date.now());
            toast.success(`Connected · ${payload.channel}`);
          } else if (payload.status === "disconnected") {
            setConnected(false);
            setConnectedSince(null);
          } else if (payload.status === "error") {
            toast.error(`Error: ${payload.error || "unknown"}`);
          }
        } else if (payload.type === "message") {
          handleIncomingMessage(payload.data);
        }
      } catch (e) { /* noop */ }
    };
    ws.onerror = () => { /* silent — reconnect loop handles it */ };
    ws.onclose = () => setConnected(false);
  };

  const handleIncomingMessage = (m) => {
    const s = settingsRef.current || {};
    const text = (m.text || "").trim();
    if (text.length < (s.min_message_length || 2)) return;
    if (text.length > (s.max_message_length || 220)) return;
    const banned = (s.banned_words || []).map((w) => w.toLowerCase());
    const low = text.toLowerCase();
    if (banned.some((w) => w && low.includes(w))) return;
    const ignored = (s.ignored_users || []).map((u) => u.toLowerCase());
    if (ignored.includes((m.username || "").toLowerCase())) return;

    const item = { ...m, status: "queued" };
    setMessages((prev) => [item, ...prev].slice(0, 200));
    if (agentEnabledRef.current) {
      queueRef.current.push(item);
      processQueue();
    }
  };

  const processQueue = async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    while (queueRef.current.length > 0 && agentEnabledRef.current) {
      const msg = queueRef.current.shift();
      setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, status: "active" } : m)));
      try {
        const s = settingsRef.current || {};
        let textToSpeak = s.read_username !== false ? `${msg.username} dit: ${msg.text}` : msg.text;
        if (s.smart_mode) {
          try {
            const comm = await api.post("/ai/comment", { username: msg.username, message: msg.text, language: "fr" });
            if (comm.data?.comment) textToSpeak += `. ${comm.data.comment}`;
          } catch (e) { /* noop */ }
        }
        setSpeaking(true);
        const r = await api.post("/tts/generate", {
          session_id: sessionId,
          text: textToSpeak,
          voice_id: s.voice_id,
          api_key: s.elevenlabs_api_key,
          stability: s.stability,
          similarity_boost: s.similarity_boost,
          message_id: msg.id,
        });
        await playAudio(r.data.audio_url);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "TTS failed");
      } finally {
        setSpeaking(false);
        setMessages((prev) => prev.map((m) => (m.id === msg.id ? { ...m, status: "read" } : m)));
      }
    }
    processingRef.current = false;
    refreshStats();
  };

  const playAudio = (src) =>
    new Promise((resolve) => {
      const el = audioRef.current;
      if (!el) return resolve();
      el.src = src;
      el.onended = resolve;
      el.onerror = resolve;
      el.play().catch(() => resolve());
    });

  const handleConnect = async (platform, channelInput) => {
    try {
      await api.post("/chat/connect", { session_id: sessionId, platform, channel: channelInput });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connect failed");
    }
  };

  const handleDisconnect = async () => {
    try { await api.post(`/chat/disconnect/${sessionId}`); } catch (e) { /* noop */ }
    setConnected(false);
    setConnectedSince(null);
    setAgentEnabled(false);
    queueRef.current = [];
    toast.info("Disconnected");
  };

  const skipCurrent = () => {
    const el = audioRef.current;
    if (el) {
      try { el.pause(); el.currentTime = 1e9; } catch (e) { /* noop */ }
    }
    toast.info("Skipped");
  };

  const replayMessage = (m) => {
    if (!agentEnabledRef.current) {
      toast.info("Start the agent to replay");
      return;
    }
    queueRef.current.push({ ...m, id: `${m.id}-r${Date.now()}` });
    processQueue();
  };

  const copyObsUrl = () => {
    const url = `${window.location.origin}/overlay/${sessionId}`;
    navigator.clipboard?.writeText(url).then(
      () => toast.success("OBS overlay link copied"),
      () => toast.error("Copy failed — URL: " + url),
    );
  };

  const downloadTranscript = () => {
    const url = `${BACKEND_URL}/api/sessions/${sessionId}/export.zip`;
    const a = document.createElement("a");
    a.href = url;
    a.download = `lucru-session.zip`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Download started");
  };

  const tryDemoChat = async () => {
    try {
      // Make sure the agent is on so TTS fires on each incoming demo message
      if (settingsRef.current?.elevenlabs_api_key && !agentEnabledRef.current) {
        setAgentEnabled(true);
      }
      await api.post("/demo/fake-chat", { session_id: sessionId, platform: "demo", channel: "demo" });
      toast.success("Demo chat starting · listen to Lyra 🎙️");
    } catch (e) {
      toast.error("Could not start demo");
    }
  };

  const toggleMute = () => {
    setMuted((v) => {
      const nv = !v;
      if (audioRef.current) {
        audioRef.current.muted = nv;
        if (nv) {
          // Also cut the current playback instantly
          try { audioRef.current.pause(); } catch (e) { /* noop */ }
        }
      }
      toast.info(nv ? "Voice muted" : "Voice unmuted");
      return nv;
    });
  };

  const toggleAgent = () => {
    const s = settingsRef.current || {};
    if (!s.elevenlabs_api_key) {
      toast.error("Add your ElevenLabs API key in Settings");
      setShowSettings(true);
      return;
    }
    setAgentEnabled((v) => {
      const nv = !v;
      if (nv) toast.success("Agent live"); else toast.info("Agent paused");
      return nv;
    });
  };

  const saveSettings = async (next) => {
    const payload = { ...settings, ...next, session_id: sessionId };
    const r = await api.post("/settings", payload);
    setSettings(r.data);
    toast.success("Saved");
  };

  if (!settings) return <div className="p-10 text-white/60">Loading…</div>;

  const AgentCard = (
    <div className="glass rounded-2xl p-5 md:p-6 relative overflow-hidden flex-1 flex flex-col items-center justify-center min-h-[220px]">
      <div className="absolute top-3 left-3 flex items-center gap-1.5 text-[10px] text-white/50">
        <SparkleIcon size={11} /> AI Co-host
      </div>
      <div className="absolute top-3 right-3 flex items-center gap-1.5 text-[10px]">
        <span className={`w-1.5 h-1.5 rounded-full ${speaking ? "bg-[#FF007F] animate-pulse" : agentEnabled ? "bg-[#39FF14]" : "bg-white/20"}`} />
        <span className="text-white/60">{speaking ? "Speaking" : agentEnabled ? "Listening" : "Idle"}</span>
      </div>
      <AgentVisualizer active={speaking} enabled={agentEnabled} />
      <div className="mt-4 font-display text-xl md:text-2xl font-black tracking-tight">
        {settings.voice_name || "Nova"}
      </div>
      <div className="text-[11px] text-white/50 mt-1">
        {settings.smart_mode ? "Smart mode · reads + reacts" : "Read-only mode"}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen md:h-screen w-full flex flex-col overflow-hidden bg-[#05050A]">
      <audio ref={audioRef} data-testid="tts-audio" />
      <header className="glass border-b border-[#222233] flex items-center justify-between px-4 md:px-6 py-3 shrink-0">
        <div className="flex items-center gap-2 md:gap-3">
          <Link to="/" className="flex items-center gap-2" data-testid="back-to-landing">
            <CaretLeftIcon size={14} />
            <Logo size={32} withBackground />
            <div className="flex flex-col leading-none">
              <span className="font-display font-black text-sm md:text-base">LUCRÜ</span>
            </div>
          </Link>
          <div className="h-5 w-px bg-[#222233] mx-1 hidden md:block" />
          <div className="hidden md:flex items-center gap-2 text-xs">
            <span className={`w-2 h-2 rounded-full ${connected ? "bg-[#39FF14] animate-pulse" : "bg-white/20"}`} />
            <span className="text-white/60">{connected ? `· #${channel}` : "Not connected"}</span>
            {connected && uptime && (
              <span className="flex items-center gap-1 ml-2 text-white/40 font-mono" data-testid="session-uptime">
                <TimerIcon size={11} /> {uptime}
              </span>
            )}
          </div>
          <div className="hidden md:block ml-2">
            <TrialBadge sessionId={sessionId} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="md:hidden">
            <TrialBadge sessionId={sessionId} compact />
          </div>
          <button
            onClick={toggleMute}
            data-testid="mute-button"
            title={muted ? "Unmute voice" : "Mute voice"}
            className={`btn-pill ${muted ? "btn-primary" : "btn-ghost"}`}
          >
            {muted ? <SpeakerSlashIcon size={14} weight="fill" /> : <SpeakerHighIcon size={14} />}
            <span className="hidden sm:inline">{muted ? "Muted" : "Live"}</span>
          </button>
          {connected && (
            <button onClick={toggleAgent} className={`btn-pill ${agentEnabled ? "btn-primary" : "btn-ghost"}`} data-testid="toggle-agent-button">
              {agentEnabled ? <><StopIcon size={14} weight="fill" /> <span className="hidden sm:inline">Pause</span></> : <><PlayIcon size={14} weight="fill" /> <span className="hidden sm:inline">Start</span></>}
            </button>
          )}
          {connected && speaking && (
            <button onClick={skipCurrent} className="btn-pill btn-ghost" data-testid="skip-button" title="Skip current message">
              <SkipForwardIcon size={14} weight="fill" /> <span className="hidden sm:inline">Skip</span>
            </button>
          )}
          <button onClick={copyObsUrl} className="btn-pill btn-ghost hidden md:inline-flex" data-testid="copy-obs-url" title="Copy OBS overlay URL">
            <CopyIcon size={14} /> <span className="hidden lg:inline">OBS</span>
          </button>
          <button onClick={downloadTranscript} className="btn-pill btn-ghost hidden md:inline-flex" data-testid="download-transcript" title="Download chat + audio (zip)">
            <DownloadSimpleIcon size={14} /> <span className="hidden lg:inline">Export</span>
          </button>
          <button onClick={() => setRecapOpen(true)} className="btn-pill btn-ghost hidden md:inline-flex" data-testid="open-recap" title="Share your weekly recap">
            <ChartBarIcon size={14} /> <span className="hidden lg:inline">Recap</span>
          </button>
          <button onClick={() => setShowSettings(true)} className="btn-pill btn-ghost" data-testid="open-settings-button">
            <GearIcon size={14} /> <span className="hidden sm:inline">Settings</span>
          </button>
        </div>
      </header>

      {/* Desktop grid */}
      <div className="hidden md:grid flex-1 grid-cols-12 gap-4 p-4 overflow-hidden">
        <aside className="col-span-3 flex flex-col gap-4 overflow-y-auto">
          <ConnectPanel connected={connected} channel={channel} onConnect={handleConnect} onDisconnect={handleDisconnect} onTryDemo={tryDemoChat} />
          {connected && <RelayShare sessionId={sessionId} />}
          <ProTrialHook sessionId={sessionId} />
          <VipStatusCard sessionId={sessionId} />
          <HighlightsFeed sessionId={sessionId} />
          <StatsPanel stats={stats} />
        </aside>
        <section className="col-span-6 flex flex-col gap-4 overflow-hidden">
          {AgentCard}
          <div className="glass rounded-2xl p-4 h-[200px] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-xs text-white/50">
                <ChartLineUpIcon size={12} /> Live activity
              </div>
              <div className="text-xs text-white/40">{messages.length} msgs</div>
            </div>
            <div className="flex items-end gap-1 h-full">
              {Array.from({ length: 60 }).map((_, i) => {
                const h = ((Math.sin(i * 0.5 + messages.length) + 1) / 2) * 60 + 10;
                return <div key={i} className="viz-bar" style={{ height: h }} />;
              })}
            </div>
          </div>
        </section>
        <aside className="col-span-3 flex flex-col gap-4 overflow-hidden">
          <ChatQueue messages={messages} onReplay={replayMessage} />
        </aside>
      </div>

      {/* Mobile layout */}
      <div className="flex-1 md:hidden flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto p-3 pb-20">
          {mobileTab === "connect" && (
            <div className="space-y-3">
              <ConnectPanel connected={connected} channel={channel} onConnect={handleConnect} onDisconnect={handleDisconnect} onTryDemo={tryDemoChat} />
              {connected && <RelayShare sessionId={sessionId} />}
              <ProTrialHook sessionId={sessionId} />
              <VipStatusCard sessionId={sessionId} />
              <HighlightsFeed sessionId={sessionId} />
              <StatsPanel stats={stats} />
            </div>
          )}
          {mobileTab === "agent" && (
            <div className="space-y-3">
              {AgentCard}
              <div className="text-center text-[10px] text-white/40">
                {connected ? `Connected · ${channel}` : "Not connected"}
              </div>
            </div>
          )}
          {mobileTab === "chat" && <ChatQueue messages={messages} onReplay={replayMessage} />}
        </div>
        <nav className="glass border-t border-[#222233] grid grid-cols-3 shrink-0">
          {[
            { id: "connect", label: "Connect", icon: <PlugIcon size={18} /> },
            { id: "agent", label: "Agent", icon: <BroadcastIcon size={18} /> },
            { id: "chat", label: "Chat", icon: <ChatCircleTextIcon size={18} /> },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setMobileTab(t.id)}
              data-testid={`mobile-tab-${t.id}`}
              className={`flex flex-col items-center gap-0.5 py-3 text-[10px] uppercase tracking-wider transition-colors ${
                mobileTab === t.id ? "text-[#00E5FF]" : "text-white/50"
              }`}
            >
              {t.icon}
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      <AnimatePresence>
        {showSettings && (
          <SettingsPanel settings={settings} sessionId={sessionId} onClose={() => setShowSettings(false)} onSave={saveSettings} />
        )}
      </AnimatePresence>
      <RecapShareModal open={recapOpen} onClose={() => setRecapOpen(false)} sessionId={sessionId} channel={channel} />
      <FirstRunWelcome
        hasKey={!!settings?.elevenlabs_api_key}
        onOpenSettings={() => setShowSettings(true)}
        onTryDemo={tryDemoChat}
      />
    </div>
  );
};

export default Dashboard;
